/**
 * AdminEmailLogs — Iter89e (2026-02-20)
 *
 * Mandantenweite E-Mail-Send-Übersicht für Manager/Admin. Zeigt alle
 * versendeten Mails (Auftragsbestätigungen, Anzahlungsrechnungen, BzA-Pakete,
 * KfW-Bescheide, Freigabe-Mails etc.) mit Filter nach Kind, Status,
 * Zeitraum und Freitext-Suche. Direktlink zum Projekt in jeder Zeile.
 *
 * User-Story (Christof, 2026-02-20): „Wenn eine Auftragsbestätigung
 * versendet wird, will ich sehen, wer sie wann bekommen hat — auch
 * projektübergreifend."
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Mail, CheckCircle2, XCircle, Filter, Search, Loader2, ArrowLeft,
  FileText, Receipt, Send, RefreshCw, Download,
} from "lucide-react";
import api from "@/lib/api";

// Kind-Katalog — die zentralen Sende-Ereignisse, die in email_logs auftauchen.
const KIND_META = {
  order_confirmation:      { label: "Auftragsbestätigung (auto)", icon: FileText, accent: "text-sky-300" },
  order_confirmation_pdf:  { label: "Auftragsbestätigung",        icon: FileText, accent: "text-sky-300" },
  customer_bza_package:    { label: "BzA-Kundenpaket",             icon: Send,     accent: "text-sky-400" },
  down_payment_invoice:    { label: "Anzahlungsrechnung",         icon: Receipt,  accent: "text-emerald-300" },
  final_invoice:           { label: "Schlussrechnung",            icon: Receipt,  accent: "text-emerald-300" },
  bza_package:             { label: "BzA-Paket",                  icon: Send,     accent: "text-amber-300" },
  orientation_offer_sent:  { label: "Orientierungsangebot",       icon: Send,     accent: "text-violet-300" },
  offer_send:              { label: "Angebots-Versand",           icon: Send,     accent: "text-blue-300" },
};

function kindMeta(kind) {
  return KIND_META[kind] || { label: kind || "—", icon: Mail, accent: "text-zinc-300" };
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminEmailLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [countOk, setCountOk] = useState(0);
  const [countFailed, setCountFailed] = useState(0);

  const [kind, setKind] = useState("");
  const [ok, setOk] = useState("");   // "" | "ok" | "failed"
  const [days, setDays] = useState(30);
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");

  // Debounce der Suche (300 ms) — sonst feuert jeder Keystroke einen Request.
  useEffect(() => {
    const t = setTimeout(() => setQDeb(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { days, limit: 400 };
      if (kind) params.kind = kind;
      if (ok === "ok") params.ok = true;
      else if (ok === "failed") params.ok = false;
      if (qDeb) params.q = qDeb;
      const r = await api.get("/admin/email-logs", { params });
      setLogs(Array.isArray(r.data?.logs) ? r.data.logs : []);
      setCount(r.data?.count ?? 0);
      setCountOk(r.data?.count_ok ?? 0);
      setCountFailed(r.data?.count_failed ?? 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Sende-Logs nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, ok, days, qDeb]);

  const csvHref = useMemo(() => {
    // Einfacher CSV-Export clientseitig aus den geladenen Logs.
    const rows = [
      ["Zeitpunkt", "Kind", "Status", "Empfänger", "Betreff", "Projekt", "Kunde", "Provider", "Fehler"],
      ...logs.map((l) => [
        fmtDateTime(l.sent_at),
        l.kind || "",
        l.ok ? "OK" : "FEHLER",
        l.recipient_email || "",
        (l.subject || "").replace(/[\r\n]+/g, " "),
        l.project_number || "",
        l.customer_name || "",
        l.provider || "",
        (l.error || "").replace(/[\r\n]+/g, " "),
      ]),
    ];
    const csv = rows.map((r) =>
      r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    return `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  }, [logs]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 p-4 sm:p-6" data-testid="admin-email-logs-page">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard"
            className="h-9 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-200 text-[12.5px] inline-flex items-center gap-1.5"
          >
            <ArrowLeft size={13} /> Dashboard
          </Link>
          <h1 className="font-display text-xl sm:text-2xl font-bold inline-flex items-center gap-2">
            <Mail className="text-[#00E575]" size={22} /> E-Mail-Sende-Übersicht
          </h1>
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-email-logs-refresh"
            className="ml-auto h-9 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-100 text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {loading ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>} Neu laden
          </button>
        </div>

        {/* KPI-Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-3">
            <div className="text-[11px] text-zinc-400 uppercase tracking-wider font-semibold">Gesamt (Zeitraum)</div>
            <div className="font-display text-2xl font-bold mt-1" data-testid="stat-total">{count}</div>
          </div>
          <div className="bg-[#0B1F14] border border-[#00E575]/30 rounded-xl p-3">
            <div className="text-[11px] text-[#00E575]/80 uppercase tracking-wider font-semibold">Erfolgreich</div>
            <div className="font-display text-2xl font-bold mt-1 text-[#00E575]" data-testid="stat-ok">{countOk}</div>
          </div>
          <div className="bg-red-950/40 border border-red-500/40 rounded-xl p-3">
            <div className="text-[11px] text-red-300/80 uppercase tracking-wider font-semibold">Fehlgeschlagen</div>
            <div className="font-display text-2xl font-bold mt-1 text-red-300" data-testid="stat-failed">{countFailed}</div>
          </div>
        </div>

        {/* Filter-Bar */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
          <label className="col-span-1 sm:col-span-2 relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suche: Empfänger, Betreff, Projekt-Nr., Kunde…"
              data-testid="admin-email-logs-search"
              className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg pl-9 pr-3 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none"
            />
          </label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            data-testid="admin-email-logs-kind"
            className="h-10 bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none"
          >
            <option value="">Alle Ereignis-Typen</option>
            <option value="order_confirmation_pdf">Auftragsbestätigung</option>
            <option value="customer_bza_package">BzA-Kundenpaket</option>
            <option value="order_confirmation">Auftragsbestätigung (auto)</option>
            <option value="down_payment_invoice">Anzahlungsrechnung</option>
            <option value="final_invoice">Schlussrechnung</option>
            <option value="bza_package">BzA-Paket</option>
            <option value="orientation_offer_sent">Orientierungsangebot</option>
            <option value="offer_send">Angebots-Versand</option>
          </select>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={ok}
              onChange={(e) => setOk(e.target.value)}
              data-testid="admin-email-logs-status"
              className="h-10 bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none"
            >
              <option value="">Alle Status</option>
              <option value="ok">Nur erfolgreich</option>
              <option value="failed">Nur fehlgeschlagen</option>
            </select>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="admin-email-logs-days"
              className="h-10 bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none"
            >
              <option value={7}>Letzte 7 Tage</option>
              <option value={30}>Letzte 30 Tage</option>
              <option value={90}>Letzte 90 Tage</option>
              <option value={365}>Letzte 12 Monate</option>
            </select>
          </div>
        </div>

        {/* Tabelle */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-zinc-400 text-[13px]">
              <Loader2 size={16} className="animate-spin mr-2"/> Lade Sende-Logs…
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-zinc-400 text-[13px]" data-testid="admin-email-logs-empty">
              <Filter size={20} className="mx-auto text-zinc-600 mb-2"/>
              Keine Einträge für die gewählten Filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="admin-email-logs-table">
                <thead className="bg-[#09090B] border-b border-zinc-800 text-[11px] uppercase tracking-wider text-zinc-400">
                  <tr>
                    <th className="text-left py-2 px-3">Zeitpunkt</th>
                    <th className="text-left py-2 px-3">Ereignis</th>
                    <th className="text-left py-2 px-3">Empfänger</th>
                    <th className="text-left py-2 px-3">Betreff</th>
                    <th className="text-left py-2 px-3">Projekt / Kunde</th>
                    <th className="text-left py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => {
                    const meta = kindMeta(l.kind);
                    const Icon = meta.icon;
                    return (
                      <tr
                        key={l.id}
                        data-testid={`admin-email-log-row-${l.id}`}
                        className="border-b border-zinc-800/60 hover:bg-[#0F0F11]"
                      >
                        <td className="py-2 px-3 font-mono text-zinc-300 whitespace-nowrap">{fmtDateTime(l.sent_at)}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center gap-1.5 ${meta.accent}`}>
                            <Icon size={13}/> {meta.label}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-zinc-200">{l.recipient_email || <span className="text-zinc-500">—</span>}</td>
                        <td className="py-2 px-3 text-zinc-300 max-w-[280px] truncate" title={l.subject}>
                          {l.subject || "—"}
                        </td>
                        <td className="py-2 px-3">
                          {l.project_id ? (
                            <Link
                              to={`/projects/${l.project_id}`}
                              className="text-[#0EA5E9] hover:underline font-mono"
                              data-testid={`admin-email-log-project-${l.id}`}
                            >
                              {l.project_number || l.project_id.slice(0, 8)}
                            </Link>
                          ) : <span className="text-zinc-500">—</span>}
                          {l.customer_name && (
                            <div className="text-[11px] text-zinc-500 truncate max-w-[180px]">{l.customer_name}</div>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          {l.ok ? (
                            <span className="inline-flex items-center gap-1 text-[#00E575] font-semibold">
                              <CheckCircle2 size={13}/> versendet
                            </span>
                          ) : (
                            <div className="text-red-300">
                              <span className="inline-flex items-center gap-1 font-semibold">
                                <XCircle size={13}/> Fehler
                              </span>
                              {l.error && (
                                <div className="text-[11px] text-red-200/70 max-w-[220px] truncate" title={l.error}>
                                  {l.error}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CSV-Export */}
        {logs.length > 0 && (
          <div className="flex justify-end">
            <a
              href={csvHref}
              download={`novatus_email_logs_${new Date().toISOString().slice(0, 10)}.csv`}
              data-testid="admin-email-logs-csv"
              className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12.5px] inline-flex items-center gap-1.5"
            >
              <Download size={13}/> CSV-Export ({logs.length})
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
